import type { JSX, VNode } from '@stencil/core';
import { Component, Element, h, Method, Prop, State, Watch } from '@stencil/core';
import clsx from '../../utils/clsx';

import type {
	AutoCompletePropType,
	DisabledPropType,
	FocusableElement,
	HideLabelPropType,
	HideMsgPropType,
	HintPropType,
	IconsHorizontalPropType,
	InputNumberAPI,
	InputNumberStates,
	InputTypeOnDefault,
	InternalButtonProps,
	LabelWithExpertSlotPropType,
	MsgPropType,
	NamePropType,
	NumberString,
	PlaceholderPropType,
	ReadOnlyPropType,
	RequiredPropType,
	ShortKeyPropType,
	Stringified,
	SuggestionsPropType,
	SyncValueBySelectorPropType,
	TooltipAlignPropType,
} from '../../schema';

import KolFormFieldStateWrapperFc, { type FormFieldStateWrapperProps } from '../../functional-component-wrappers/FormFieldStateWrapper/FormFieldStateWrapper';
import KolInputContainerFc from '../../functional-component-wrappers/InputContainerStateWrapper/InputContainerStateWrapper';
import KolInputStateWrapperFc, { type InputStateWrapperProps } from '../../functional-component-wrappers/InputStateWrapper/InputStateWrapper';
import { IconFC } from '../../internal/functional-components/icon/component';
import { nonce } from '../../utils/dev.utils';
import { delegateClick, setClick } from '../../utils/element-click';
import { delegateFocus, setFocus } from '../../utils/element-focus';
import { propagateSubmitEventToForm } from '../form/controller';
import { InputNumberController } from './controller';

/**
 * The **Number** input type creates an input field for numeric values. Use the `_min`, `_max`, and `_step` properties to restrict the accepted value range.
 *
 * @slot - The label of the input field.
 */
@Component({
	tag: 'kol-input-number',
	styleUrls: {
		default: './style.scss',
	},
	shadow: true,
})
export class KolInputNumber implements InputNumberAPI, FocusableElement {
	@Element() private readonly host?: HTMLKolInputNumberElement;
	private inputRef?: HTMLInputElement;

	private readonly setInputRef = (ref?: HTMLInputElement) => {
		this.inputRef = ref;
	};

	/**
	 * Returns the current value.
	 */
	@Method()
	// eslint-disable-next-line @typescript-eslint/require-await
	public async getValue(): Promise<number | NumberString | null> {
		return this.remapValue(this.state._value);
	}

	/**
	 * Sets focus on the internal element.
	 */
	@Method()
	public async focus() {
		return delegateFocus(this.host!, () => setFocus(this.inputRef!));
	}

	/**
	 * Clicks the primary interactive element inside this component.
	 */
	@Method()
	public async click(): Promise<void> {
		return delegateClick(this.host!, async () => setClick(this.inputRef!));
	}

	private setInitialValueType(value?: number | NumberString | null) {
		if (this.controller.isNumberString(value)) {
			this._initialValueType = 'NumberString';
		} else if (typeof value === 'number' && !isNaN(value)) {
			this._initialValueType = 'number';
		} else {
			this._initialValueType = 'null';
		}
	}

	/**
	 * Map the value to the initial format. E.g., if the user provided a string initially, return a string.
	 * @param value
	 * @private
	 */
	private remapValue(value?: number | null): number | NumberString | null {
		if (value === undefined || value === null) {
			return null;
		} else if (this._initialValueType === 'NumberString') {
			return String(value) as NumberString;
		}
		return value;
	}

	private readonly onInput = (event: InputEvent) => {
		const newValue = this.inputRef?.value;
		this._value = this.remapValue(newValue === '' ? null : Number(newValue));
		this.controller.onFacade.onInput(event, true, this._value);
	};

	private readonly onChange = (event: Event) => {
		const newValue = this.inputRef?.value;
		const mappedValue = this.remapValue(newValue === '' ? null : Number(newValue));
		this.controller.onFacade.onChange(event, mappedValue);
	};

	private readonly onKeyDown = (event: KeyboardEvent) => {
		this.controller.onFacade.onKeyDown(event);

		if (event.code === 'Enter' || event.code === 'NumpadEnter') {
			propagateSubmitEventToForm({
				form: this.host,
				ref: this.inputRef,
			});
		}
	};

	private getFormFieldProps(): FormFieldStateWrapperProps {
		return {
			state: this.state,
			class: clsx('kol-input-number', 'number', {
				'has-value': this.state._hasValue,
			}),
			tooltipAlign: this._tooltipAlign,
			alert: this.showAsAlert(),
		};
	}

	private getInputProps(): InputStateWrapperProps {
		return {
			ref: this.setInputRef,
			state: this.state,
			type: 'number',
			...this.controller.onFacade,
			onInput: this.onInput,
			onChange: this.onChange,
			onKeyDown: this.onKeyDown,
			onFocus: (event: FocusEvent) => {
				const prevFocusElem = event.relatedTarget as HTMLElement;
				const isStepButton = prevFocusElem?.classList.contains('kol-input-number__step-button');
				if (!isStepButton) {
					this.controller.onFacade.onFocus(event);
					this.inputHasFocus = true;
				}
			},
			onBlur: (event: FocusEvent) => {
				const nextFocusElem = event.relatedTarget as HTMLElement;
				const isStepButton = nextFocusElem?.classList.contains('kol-input-number__step-button');
				if (!isStepButton) {
					this.controller.onFacade.onBlur(event);
					this.inputHasFocus = false;
				}
			},
		};
	}

	private getStepUpButton(): VNode | null {
		if (this._disabled || this._readOnly) {
			return null;
		}

		return (
			<button
				type="button"
				tabIndex={-1}
				class="kol-input-number__step-button kol-input-number__step-button-up kol-input-container__smart-button"
				data-testid="kol-input-number-step-up"
				onClick={(event: MouseEvent): void => {
					this.inputRef?.stepUp();
					// Manually trigger onInput since stepUp() doesn't fire input events
					const newValue = this.inputRef?.value;
					this._value = this.remapValue(newValue === '' ? null : Number(newValue));
					// Pass MouseEvent as Event - onInput handler accepts generic Event type
					this.controller.onFacade.onInput(event, true, this._value);
					// native number buttons also throw the change event on every click
					this.controller.onFacade.onChange(event, this._value);
					this.inputRef?.focus();
				}}
				disabled={this._disabled || this._readOnly}
			>
				<IconFC icons="kolicon-plus" label="" />
			</button>
		);
	}

	private getStepDownButton(): VNode | null {
		if (this._disabled || this._readOnly) {
			return null;
		}

		return (
			<button
				type="button"
				tabIndex={-1}
				class="kol-input-number__step-button kol-input-number__step-button-down kol-input-container__smart-button"
				data-testid="kol-input-number-step-down"
				onClick={(event: MouseEvent): void => {
					this.inputRef?.stepDown();
					// Manually trigger onInput since stepDown() doesn't fire input events
					const newValue = this.inputRef?.value;
					this._value = this.remapValue(newValue === '' ? null : Number(newValue));
					// Pass MouseEvent as Event - onInput handler accepts generic Event type
					this.controller.onFacade.onInput(event, true, this._value);
					// native number buttons also throw the change event on every click
					this.controller.onFacade.onChange(event, this._value);
					this.inputRef?.focus();
				}}
				disabled={this._disabled || this._readOnly}
			>
				<IconFC icons="kolicon-minus" label="" />
			</button>
		);
	}

	public render(): JSX.Element {
		return (
			<KolFormFieldStateWrapperFc {...this.getFormFieldProps()}>
				<KolInputContainerFc state={this.state} startAdornment={this.getStepDownButton()} endAdornment={this.getStepUpButton()}>
					<KolInputStateWrapperFc {...this.getInputProps()} />
				</KolInputContainerFc>
			</KolFormFieldStateWrapperFc>
		);
	}

	private readonly controller: InputNumberController;

	/**
	 * Defines the key combination that can be used to trigger or focus the component's interactive element.
	 */
	@Prop() public _accessKey?: string;

	/**
	 * Defines whether the input can be auto-completed.
	 */
	@Prop() public _autoComplete?: AutoCompletePropType = 'off';

	/**
	 * Makes the element not focusable and ignore all events.
	 * @TODO: Change type back to `DisabledPropType` after Stencil#4663 has been resolved.
	 */
	@Prop() public _disabled?: boolean = false;

	/**
	 * Hides the error message but leaves it in the DOM for the input's aria-describedby.
	 * @TODO: Change type back to `HideMsgPropType` after Stencil#4663 has been resolved.
	 */
	@Prop() public _hideMsg?: boolean = false;

	/**
	 * Hides the caption by default and displays the caption text with a tooltip when the
	 * interactive element is focused or the mouse is over it.
	 * @TODO: Change type back to `HideLabelPropType` after Stencil#4663 has been resolved.
	 */
	@Prop() public _hideLabel?: boolean = false;

	/**
	 * Defines the hint text.
	 */
	@Prop() public _hint?: string = '';

	/**
	 * Defines the icon classnames (e.g. `_icons="fa-solid fa-user"`).
	 */
	@Prop() public _icons?: IconsHorizontalPropType;

	/**
	 * Defines the visible or semantic label of the component (e.g. aria-label, label, headline, caption, summary, etc.). Set to `false` to enable the expert slot.
	 */
	@Prop() public _label!: LabelWithExpertSlotPropType;

	/**
	 * Defines the maximum value of the element.
	 */
	@Prop() public _max?: number | NumberString;

	/**
	 * Defines the smallest possible input value.
	 */
	@Prop() public _min?: number | NumberString;

	/**
	 * Defines the properties for a message rendered as Alert component.
	 */
	@Prop() public _msg?: Stringified<MsgPropType>;

	/**
	 * Defines the technical name of an input field.
	 */
	@Prop() public _name?: NamePropType;

	/**
	 * Gibt die EventCallback-Funktionen für das Input-Event an.
	 */
	@Prop() public _on?: InputTypeOnDefault;

	/**
	 * Defines the placeholder for input field. To be shown when there's no value.
	 */
	@Prop() public _placeholder?: string;

	/**
	 * Makes the input element read only.
	 * @TODO: Change type back to `ReadOnlyPropType` after Stencil#4663 has been resolved.
	 */
	@Prop() public _readOnly?: boolean = false;

	/**
	 * Makes the input element required.
	 * @TODO: Change type back to `RequiredPropType` after Stencil#4663 has been resolved.
	 */
	@Prop() public _required?: boolean = false;

	/**
	 * Adds a visual shortcut hint after the label and instructs the screen reader to read the shortcut aloud.
	 */
	@Prop() public _shortKey?: ShortKeyPropType;

	/**
	 * Allows to add a button with an arbitrary action within the element (_hide-label only).
	 */
	@Prop() public _smartButton?: Stringified<InternalButtonProps>;

	/**
	 * Suggestions to provide for the input.
	 */
	@Prop() public _suggestions?: SuggestionsPropType;

	/**
	 * Defines the step size for value changes.
	 */
	@Prop() public _step?: number | NumberString;

	/**
	 * Selector for synchronizing the value with another input element.
	 * @internal
	 */
	@Prop() public _syncValueBySelector?: SyncValueBySelectorPropType;

	/**
	 * Defines where to show the Tooltip preferably: top, right, bottom or left.
	 */
	@Prop() public _tooltipAlign?: TooltipAlignPropType = 'top';

	/**
	 * Shows if the input was touched by a user.
	 * @TODO: Change type back to `TouchedPropType` after Stencil#4663 has been resolved.
	 */
	@Prop({ mutable: true, reflect: true }) public _touched?: boolean = false;

	/**
	 * Defines the value of the element.
	 */
	@Prop({ mutable: true, reflect: true }) public _value?: number | NumberString | null;

	@State() public state: InputNumberStates = {
		_hasValue: false,
		_hideMsg: false,
		_id: `id-${nonce()}`,
		_label: '', // ⚠ required
		_suggestions: [],
	};
	@State() private _initialValueType: 'number' | 'NumberString' | 'null' = 'null';
	@State() private inputHasFocus = false;

	public constructor() {
		this.controller = new InputNumberController(this, 'number', this.host);
	}

	private showAsAlert(): boolean {
		return Boolean(this.state._touched) && !this.inputHasFocus;
	}

	@Watch('_accessKey')
	public validateAccessKey(value?: string): void {
		this.controller.validateAccessKey(value);
	}

	@Watch('_autoComplete')
	public validateAutoComplete(value?: AutoCompletePropType): void {
		this.controller.validateAutoComplete(value);
	}

	@Watch('_disabled')
	public validateDisabled(value?: DisabledPropType): void {
		this.controller.validateDisabled(value);
	}

	@Watch('_hideMsg')
	public validateHideMsg(value?: HideMsgPropType): void {
		this.controller.validateHideMsg(value);
	}

	@Watch('_hideLabel')
	public validateHideLabel(value?: HideLabelPropType): void {
		this.controller.validateHideLabel(value);
	}

	@Watch('_hint')
	public validateHint(value?: HintPropType): void {
		this.controller.validateHint(value);
	}

	@Watch('_icons')
	public validateIcons(value?: IconsHorizontalPropType): void {
		this.controller.validateIcons(value);
	}

	@Watch('_label')
	public validateLabel(value?: LabelWithExpertSlotPropType): void {
		this.controller.validateLabel(value);
	}

	@Watch('_max')
	public validateMax(value?: number | NumberString): void {
		this.controller.validateMax(value);
	}

	@Watch('_min')
	public validateMin(value?: number | NumberString): void {
		this.controller.validateMin(value);
	}

	@Watch('_msg')
	public validateMsg(value?: Stringified<MsgPropType>): void {
		this.controller.validateMsg(value);
	}

	@Watch('_name')
	public validateName(value?: string): void {
		this.controller.validateName(value);
	}

	@Watch('_on')
	public validateOn(value?: InputTypeOnDefault): void {
		this.controller.validateOn(value);
	}

	@Watch('_placeholder')
	public validatePlaceholder(value?: PlaceholderPropType): void {
		this.controller.validatePlaceholder(value);
	}

	@Watch('_readOnly')
	public validateReadOnly(value?: ReadOnlyPropType): void {
		this.controller.validateReadOnly(value);
	}

	@Watch('_required')
	public validateRequired(value?: RequiredPropType): void {
		this.controller.validateRequired(value);
	}

	@Watch('_shortKey')
	public validateShortKey(value?: ShortKeyPropType): void {
		this.controller.validateShortKey(value);
	}

	@Watch('_smartButton')
	public validateSmartButton(value?: InternalButtonProps | string): void {
		this.controller.validateSmartButton(value);
	}

	@Watch('_suggestions')
	public validateSuggestions(value?: SuggestionsPropType): void {
		this.controller.validateSuggestions(value);
	}

	@Watch('_step')
	public validateStep(value?: number | NumberString): void {
		this.controller.validateStep(value);
	}

	@Watch('_syncValueBySelector')
	public validateSyncValueBySelector(value?: SyncValueBySelectorPropType): void {
		this.controller.validateSyncValueBySelector(value);
	}

	@Watch('_touched')
	public validateTouched(value?: boolean): void {
		this.controller.validateTouched(value);
	}

	@Watch('_value')
	public validateValue(value?: number | NumberString | null): void {
		this.controller.validateValue(value);
		if (value !== undefined && value !== null) {
			// Don't switch type when value was reset to null
			this.setInitialValueType(value);
		}
	}

	public componentWillLoad(): void {
		if (this._value !== undefined) {
			this.setInitialValueType(this._value);
		}
		this._touched = this._touched === true;
		this.controller.componentWillLoad();

		this.state._hasValue = !!this.state._value;
		this.controller.addValueChangeListener((v) => (this.state._hasValue = !!v));
	}
}
